import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  User, 
  Building2, 
  Calendar,
  Briefcase,
  Loader2
} from 'lucide-react';
import Header from '../components/Header';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cashflow-596/artifacts/ipba3h4w_Gazeal%20Advance%20logo.png";

const ApplyPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    // Step 1: Personal Details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Step 2: Agency Details
    agencyName: '',
    region: '',
    address: '',
    companyNumber: '',
    // Step 3: Business Profile
    yearsInBusiness: '',
    annualListings: '',
    averageSaleValue: '',
    teamSize: '',
    referralSource: '',
    referringAgent: '',
    // Step 4: Confirmation
    agreeToMeeting: false,
    agreeToTerms: false,
    additionalNotes: '',
  });

  const totalSteps = 4;

  const steps = [
    { number: 1, title: 'Your Details', icon: User },
    { number: 2, title: 'Agency Info', icon: Building2 },
    { number: 3, title: 'Business Profile', icon: Briefcase },
    { number: 4, title: 'Book Meeting', icon: Calendar },
  ];

  const regions = [
    'Greater London',
    'South East',
    'South West',
    'East of England',
    'West Midlands',
    'East Midlands',
    'Yorkshire and the Humber',
    'North West',
    'North East',
    'Wales',
    'Scotland',
    'Northern Ireland',
  ];

  const yearsOptions = [
    'Less than 1 year',
    '1-3 years',
    '3-5 years',
    '5-10 years',
    '10+ years',
  ];

  const listingsOptions = [
    'Less than 20',
    '20-50',
    '50-100',
    '100-200',
    '200+',
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear validation error when user types
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 1:
        if (!formData.firstName.trim()) errors.firstName = 'First name is required';
        if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
        if (!formData.email.trim()) errors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Valid email is required';
        if (!formData.phone.trim()) errors.phone = 'Phone number is required';
        break;
      case 2:
        if (!formData.agencyName.trim()) errors.agencyName = 'Agency name is required';
        if (!formData.region) errors.region = 'Region is required';
        if (!formData.address.trim()) errors.address = 'Business address is required';
        break;
      case 3:
        if (!formData.yearsInBusiness) errors.yearsInBusiness = 'Years in business is required';
        if (!formData.annualListings) errors.annualListings = 'Annual listings is required';
        break;
      default:
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await fetch(`${API_URL}/api/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      // Clone response before reading to avoid "body stream already read" error
      const responseClone = response.clone();
      
      let data;
      try {
        data = await response.json();
      } catch (e) {
        // If JSON parsing fails, try to get text
        const text = await responseClone.text();
        throw new Error(text || 'Failed to submit application');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to submit application');
      }
      
      // Redirect to HubSpot calendar for booking a meeting
      window.location.href = 'https://meetings.hubspot.com/bryan-mansell/gazeal-advance';
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitError(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h3 className="text-2xl font-semibold text-white mb-2">Personal Details</h3>
              <p className="text-white/60">Tell us about yourself.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">First Name *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`custom-input ${validationErrors.firstName ? 'border-red-500' : ''}`}
                  placeholder="John"
                  required
                />
                {validationErrors.firstName && <p className="text-red-400 text-xs mt-1">{validationErrors.firstName}</p>}
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={`custom-input ${validationErrors.lastName ? 'border-red-500' : ''}`}
                  placeholder="Smith"
                  required
                />
                {validationErrors.lastName && <p className="text-red-400 text-xs mt-1">{validationErrors.lastName}</p>}
              </div>
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Email Address *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`custom-input ${validationErrors.email ? 'border-red-500' : ''}`}
                placeholder="john@smithpartners.co.uk"
                required
              />
              {validationErrors.email && <p className="text-red-400 text-xs mt-1">{validationErrors.email}</p>}
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Phone Number *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`custom-input ${validationErrors.phone ? 'border-red-500' : ''}`}
                placeholder="+44 7700 900000"
                required
              />
              {validationErrors.phone && <p className="text-red-400 text-xs mt-1">{validationErrors.phone}</p>}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h3 className="text-2xl font-semibold text-white mb-2">Agency Information</h3>
              <p className="text-white/60">Tell us about your estate agency.</p>
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Agency Name *</label>
              <input
                type="text"
                value={formData.agencyName}
                onChange={(e) => handleInputChange('agencyName', e.target.value)}
                className={`custom-input ${validationErrors.agencyName ? 'border-red-500' : ''}`}
                placeholder="Smith & Partners Estate Agents"
                required
              />
              {validationErrors.agencyName && <p className="text-red-400 text-xs mt-1">{validationErrors.agencyName}</p>}
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Region *</label>
              <select
                value={formData.region}
                onChange={(e) => handleInputChange('region', e.target.value)}
                className={`custom-input bg-transparent ${validationErrors.region ? 'border-red-500' : ''}`}
                required
              >
                <option value="" className="bg-[#121212]">Select your region</option>
                {regions.map((region) => (
                  <option key={region} value={region} className="bg-[#121212]">
                    {region}
                  </option>
                ))}
              </select>
              {validationErrors.region && <p className="text-red-400 text-xs mt-1">{validationErrors.region}</p>}
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Business Address *</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className={`custom-input ${validationErrors.address ? 'border-red-500' : ''}`}
                placeholder="123 High Street, London, SW1A 1AA"
                required
              />
              {validationErrors.address && <p className="text-red-400 text-xs mt-1">{validationErrors.address}</p>}
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Company Registration Number</label>
              <input
                type="text"
                value={formData.companyNumber}
                onChange={(e) => handleInputChange('companyNumber', e.target.value)}
                className="custom-input"
                placeholder="12345678"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h3 className="text-2xl font-semibold text-white mb-2">Business Profile</h3>
              <p className="text-white/60">Help us understand your business better.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Years in Business *</label>
                <select
                  value={formData.yearsInBusiness}
                  onChange={(e) => handleInputChange('yearsInBusiness', e.target.value)}
                  className={`custom-input bg-transparent ${validationErrors.yearsInBusiness ? 'border-red-500' : ''}`}
                  required
                >
                  <option value="" className="bg-[#121212]">Select</option>
                  {yearsOptions.map((option) => (
                    <option key={option} value={option} className="bg-[#121212]">
                      {option}
                    </option>
                  ))}
                </select>
                {validationErrors.yearsInBusiness && <p className="text-red-400 text-xs mt-1">{validationErrors.yearsInBusiness}</p>}
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Annual Listings *</label>
                <select
                  value={formData.annualListings}
                  onChange={(e) => handleInputChange('annualListings', e.target.value)}
                  className={`custom-input bg-transparent ${validationErrors.annualListings ? 'border-red-500' : ''}`}
                  required
                >
                  <option value="" className="bg-[#121212]">Select</option>
                  {listingsOptions.map((option) => (
                    <option key={option} value={option} className="bg-[#121212]">
                      {option}
                    </option>
                  ))}
                </select>
                {validationErrors.annualListings && <p className="text-red-400 text-xs mt-1">{validationErrors.annualListings}</p>}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Average Sale Value</label>
                <input
                  type="text"
                  value={formData.averageSaleValue}
                  onChange={(e) => handleInputChange('averageSaleValue', e.target.value)}
                  className="custom-input"
                  placeholder="£350,000"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Team Size</label>
                <input
                  type="text"
                  value={formData.teamSize}
                  onChange={(e) => handleInputChange('teamSize', e.target.value)}
                  className="custom-input"
                  placeholder="e.g., 5 agents"
                />
              </div>
            </div>

            <div className="p-6 bg-[#59d862]/5 border border-[#59d862]/20 rounded-2xl">
              <h4 className="text-white font-semibold mb-1">Know Someone in the Network?</h4>
              <p className="text-white/50 text-sm mb-4">
                Many of our agents join through recommendations from colleagues. If you know an agent already using Gazeal, let us know. Referrals from existing members help strengthen your application.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Were you referred by another agent?</label>
                  <select
                    value={formData.referralSource}
                    onChange={(e) => handleInputChange('referralSource', e.target.value)}
                    className="custom-input bg-transparent"
                  >
                    <option value="" className="bg-[#121212]">Select an option</option>
                    <option value="yes_referred" className="bg-[#121212]">Yes, another agent referred me</option>
                    <option value="know_agent" className="bg-[#121212]">I know an agent who uses Gazeal</option>
                    <option value="no" className="bg-[#121212]">No / Prefer not to say</option>
                  </select>
                </div>
                {(formData.referralSource === 'yes_referred' || formData.referralSource === 'know_agent') && (
                  <div className="animate-slide-up">
                    <label className="block text-white/70 text-sm mb-2">
                      {formData.referralSource === 'yes_referred' ? 'Who referred you?' : 'Which agent do you know?'}
                    </label>
                    <input
                      type="text"
                      value={formData.referringAgent}
                      onChange={(e) => handleInputChange('referringAgent', e.target.value)}
                      className="custom-input"
                      placeholder="Agent name or agency"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 bg-[#004048]/30 border border-[#59d862]/30 rounded-2xl">
              <h4 className="text-white font-semibold mb-3">Why We Ask This</h4>
              <p className="text-white/60 text-sm">
                Gazeal Advance is designed for agents who share our commitment to doing the right thing for buyers and sellers. 
                Understanding your business helps us ensure the service is a good fit and allows us to tailor our support to your needs.
              </p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h3 className="text-2xl font-semibold text-white mb-2">Schedule a Meeting</h3>
              <p className="text-white/60">We'd love to discuss how Gazeal Advance can work for your business.</p>
            </div>

            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <h4 className="text-white/60 text-sm mb-4">APPLICATION SUMMARY</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-white/60">Name</span>
                  <span className="text-white">{formData.firstName} {formData.lastName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Email</span>
                  <span className="text-white">{formData.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Agency</span>
                  <span className="text-white">{formData.agencyName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Region</span>
                  <span className="text-white">{formData.region}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Years in Business</span>
                  <span className="text-white">{formData.yearsInBusiness}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Annual Listings</span>
                  <span className="text-white">{formData.annualListings}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-6 bg-[#59d862]/10 border border-[#59d862]/30 rounded-2xl">
                <label className="flex items-start gap-4 cursor-pointer">
                  <div
                    onClick={() => handleInputChange('agreeToMeeting', !formData.agreeToMeeting)}
                    className={`w-6 h-6 border-2 flex items-center justify-center flex-shrink-0 mt-0.5 rounded-lg transition-all ${
                      formData.agreeToMeeting ? 'border-[#59d862] bg-[#59d862]' : 'border-white/40'
                    }`}
                  >
                    {formData.agreeToMeeting && <Check className="w-4 h-4 text-black" />}
                  </div>
                  <div>
                    <span className="text-white font-semibold">I agree to schedule a meeting *</span>
                    <p className="text-white/60 text-sm mt-1">
                      Our team will contact you to arrange a convenient time to discuss Gazeal Advance, answer your questions, and ensure the service is a good fit for your business.
                    </p>
                  </div>
                </label>
              </div>

              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                <label className="flex items-start gap-4 cursor-pointer">
                  <div
                    onClick={() => handleInputChange('agreeToTerms', !formData.agreeToTerms)}
                    className={`w-6 h-6 border-2 flex items-center justify-center flex-shrink-0 mt-0.5 rounded-lg transition-all ${
                      formData.agreeToTerms ? 'border-[#59d862] bg-[#59d862]' : 'border-white/40'
                    }`}
                  >
                    {formData.agreeToTerms && <Check className="w-4 h-4 text-black" />}
                  </div>
                  <span className="text-white/80 text-sm">
                    I confirm that the information provided is accurate and I agree to be contacted regarding the Gazeal Advance program. I understand that acceptance into the program is subject to review.
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-white/70 text-sm mb-2">Additional Notes (Optional)</label>
              <textarea
                value={formData.additionalNotes}
                onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                className="custom-input min-h-[100px]"
                placeholder="Tell us anything else you'd like us to know..."
              />
            </div>

            <div className="p-6 bg-[#004048]/30 border border-[#c4b9a6]/30 rounded-2xl">
              <h4 className="text-[#c4b9a6] font-semibold mb-2">Our Commitment to Ethics</h4>
              <p className="text-white/60 text-sm">
                Gazeal Advance is built on trust and doing the right thing. We partner with agents who share our values of 
                transparency, fairness, and putting clients first. The meeting helps us ensure mutual alignment and answer 
                any questions you may have.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center mb-12">
            <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors">
              <ChevronLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Apply to <span className="text-[#59d862]">Join</span>
            </h1>
            <p className="text-white/60">
              Join the waitlist for Gazeal Advance and transform your cash flow.
            </p>
          </div>

          <div className="flex items-center justify-between mb-12">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;

              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`step-indicator ${
                        isActive ? 'active' : isCompleted ? 'completed' : ''
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-5 h-5 text-[#59d862]" />
                      ) : (
                        <Icon className={`w-5 h-5 ${isActive ? 'text-black' : 'text-white/60'}`} />
                      )}
                    </div>
                    <span className={`text-xs mt-2 hidden md:block ${
                      isActive ? 'text-[#59d862]' : 'text-white/40'
                    }`}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${
                      currentStep > step.number ? 'bg-[#59d862]' : 'bg-white/20'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="glass-card p-8 md:p-12">
            {renderStep()}

            <div className="flex flex-col gap-4 mt-10 pt-8 border-t border-white/10">
              {submitError && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-center">
                  <p className="text-red-400 text-sm">{submitError}</p>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <button
                  onClick={prevStep}
                  disabled={currentStep === 1 || isSubmitting}
                  className={`flex items-center gap-2 px-6 py-3 transition-all duration-300 ${
                    currentStep === 1 || isSubmitting
                      ? 'text-white/30 cursor-not-allowed'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                  Back
                </button>

                {currentStep < totalSteps ? (
                  <button
                    onClick={nextStep}
                    className="btn-primary px-8 py-3"
                  >
                    Continue
                    <ChevronRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={!formData.agreeToMeeting || !formData.agreeToTerms || isSubmitting}
                    className={`btn-primary px-8 py-3 ${
                      !formData.agreeToMeeting || !formData.agreeToTerms || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <Check className="w-5 h-5" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8 p-6 bg-[#004048]/20 border border-white/10 rounded-2xl text-center">
            <p className="text-white/60">
              <span className="text-[#59d862] font-semibold">Already a Gazeal agent?</span>{' '}
              You can start using Gazeal Advance immediately.{' '}
              <Link to="/get-paid" className="text-[#59d862] underline hover:no-underline">
                Get Paid Now →
              </Link>
            </p>
          </div>
        </div>
      </main>

      <footer className="bg-black border-t border-white/10 py-8">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <img src={LOGO_URL} alt="Gazeal Advance" className="h-8 mx-auto mb-4" />
          <p className="text-white/40 text-sm">© {new Date().getFullYear()} Gazeal Advance. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ApplyPage;
